import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";
import { SectionEyebrow } from "@/components/Marquee";
import api from "@/lib/api";

export default function Testimonials() {
  const [reviews, setReviews] = useState(TESTIMONIALS);

  useEffect(() => {
    api.get("/reviews")
      .then((res) => {
        const fetched = res.data?.reviews || [];
        if (fetched.length > 0) {
          setReviews(fetched.map((r) => ({
            quote: r.text, name: r.name, car: r.car, rating: r.rating,
            designation: r.designation, photo: r.photo, verified: r.source === "user",
          })));
        }
      })
      .catch(() => {
        // keep the static fallback on any error
      });
  }, []);

  const items = [...reviews, ...reviews];
  return (
    <section id="testimonials" className="relative py-20 md:py-28 bg-void overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <SectionEyebrow n="Ch. XII" testId="testimonials-eyebrow">Voices of Buyers</SectionEyebrow>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.8, 0, 0.2, 1] }}
          className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl"
        >
          They walked in wary. <span className="italic gradient-text">They drove out ahead.</span>
        </motion.h2>
        <p className="mt-4 text-silver/60 max-w-xl">
          Numbers convinced you. Here's what buying it actually felt like.
        </p>
      </div>

      <div className="mt-16 relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 md:w-40 z-10 bg-gradient-to-r from-void to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 md:w-40 z-10 bg-gradient-to-l from-void to-transparent" />
        <div className="flex overflow-hidden">
          <div className="flex marquee-track gap-5">
            {items.map((t, i) => (
              <article
                key={i}
                className="w-[86vw] sm:w-[520px] shrink-0 rounded-3xl border border-white/5 bg-surface p-8 md:p-10"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 text-[#F43F5E]">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-[#F43F5E]" />
                  ))}
                </div>
                <p className="mt-5 font-serif text-2xl md:text-3xl text-white leading-snug">"{t.quote}"</p>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    {t.photo && (
                      <img
                        src={t.photo.startsWith("data:") ? t.photo : `data:image/jpeg;base64,${t.photo}`}
                        alt={t.name}
                        className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover border border-white/10 shrink-0"
                      />
                    )}
                    <div>
                      <div className="text-silver/90 flex items-center gap-1.5">
                        {t.name}
                        {t.verified && <span className="text-[9px] uppercase tracking-wider text-[#4FC3F7] border border-[#4FC3F7]/30 rounded-full px-1.5 py-0.5">Verified</span>}
                      </div>
                      {t.designation && <div className="text-xs text-silver/45">{t.designation}</div>}
                    </div>
                  </div>
                  <div className="text-silver/50">{t.car}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
